import { Banner, Button, Card, Cluster, Container, PriceGrid, Section, Stack } from "@humaniti/ui";
import { Link } from "react-router-dom";
import { SEO } from "../lib/seo";

const PLACEHOLDER_PRICING_ROWS = [
  { taskType: "Image bbox", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Polygon", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Segmentation", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Keypoint", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Text NER", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Text classification", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Sentiment", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "RLHF preference", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Writing", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Audio transcription", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  {
    taskType: "Audio classification",
    builderRate: "TBD",
    humanPayout: "TBD",
    networkFeePct: "TBD",
  },
  { taskType: "Video frame", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Video segmentation", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "3D point cloud", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Document parsing", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  { taskType: "Coreference", builderRate: "TBD", humanPayout: "TBD", networkFeePct: "TBD" },
  {
    taskType: "Relations / moderation",
    builderRate: "TBD",
    humanPayout: "TBD",
    networkFeePct: "TBD",
  },
];

const CONSENSUS_OPTIONS: { n: string; label: string; body: string }[] = [
  {
    n: "1",
    label: "Cheap-draft",
    body: "1 Human, no peer check. Internal exploration, low-stakes prototyping.",
  },
  {
    n: "3",
    label: "Default",
    body: "3 Humans, majority verifies. Balanced cost and confidence. Most workloads.",
  },
  {
    n: "5",
    label: "High-stakes",
    body: "5 Humans, supermajority verifies. Safety, medical, legal, fairness-sensitive work.",
  },
  {
    n: "7",
    label: "Max confidence",
    body: "7 Humans. Reserved for evals, gold-standard sets, regulated benchmarks.",
  },
];

const VALUE_LIST = [
  "Per-label rate paid to verified Humans.",
  "N-Human consensus — every label checked by your chosen N.",
  "Steward adjudication on disagreement.",
  "Gold-question calibration baked into every batch.",
  "Signed receipts and full audit trail.",
  "Dataset export in 16+ formats.",
  "Direct payouts via Wise, Razorpay, Rapyd, Stripe Connect, fiat to USDC.",
];

export const PricingPage = () => (
  <>
    <SEO
      title="Pricing — Pay per verified label"
      description="Per-task pricing, transparent. No setup fees. No minimums. The Humans who verify the labels get paid directly."
      path="/pricing"
    />
    {/* 1. Hero */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § Pricing
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-display)",
              lineHeight: "var(--lh-display)",
              letterSpacing: "-0.02em",
              maxWidth: 960,
            }}
          >
            Pay per verified label<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 620,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              color: "var(--fg-secondary)",
            }}
          >
            No setup. No minimums. No vendor calls.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 2. Per-task rate grid */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 01 — Per-task rates
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Per label. Per Human. Per task type<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Banner variant="info" title="Pricing under review">
            Per-task rates land before public launch. The structure below is final; the numbers are
            provisional.
          </Banner>
          <PriceGrid rows={PLACEHOLDER_PRICING_ROWS} />
        </Stack>
      </Container>
    </Section>

    {/* 3. Consensus configuration */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 02 — Consensus
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            3 Humans by default. Dial it where you need it
            <span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Cluster gap="md" align="start">
            {CONSENSUS_OPTIONS.map((c) => (
              <Card key={c.n} style={{ flex: 1, minWidth: 220 }}>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "48px",
                    fontWeight: 500,
                    lineHeight: 1,
                    color: "var(--teal)",
                    marginBottom: "var(--space-3)",
                  }}
                >
                  {c.n}
                </div>
                <h3
                  style={{
                    margin: "0 0 var(--space-2)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                    fontSize: "var(--fs-h3)",
                  }}
                >
                  {c.label}
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  {c.body}
                </p>
              </Card>
            ))}
          </Cluster>
          <Link to="/how-it-works">Read how consensus works →</Link>
        </Stack>
      </Container>
    </Section>

    {/* 4. The network fee */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 03 — Network fee
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What the network keeps<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            The Network takes a transparent percentage of every Builder spend to fund operations,
            security, anti-gaming, payment rails, and the public Roadmap. The remainder goes
            directly to the Humans who verified the labels. Final percentage published before
            launch.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 5. What every dollar buys */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 04 — Inclusions
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What every dollar buys<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <ul
            style={{
              margin: 0,
              paddingLeft: "var(--space-5)",
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
            }}
          >
            {VALUE_LIST.map((item) => (
              <li key={item} style={{ padding: "var(--space-1) 0" }}>
                {item}
              </li>
            ))}
          </ul>
        </Stack>
      </Container>
    </Section>

    {/* 6. Closing CTA */}
    <Section style={{ background: "var(--ink)", color: "var(--paper)" }}>
      <Container size="full">
        <Stack gap="lg" alignItems="center" style={{ textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
              maxWidth: 720,
            }}
          >
            Try it with 100 labels<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
            <Button>Hire Humans →</Button>
          </Link>
        </Stack>
      </Container>
    </Section>
  </>
);
